import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InitialsAvatar } from "@shared/components";
import { checkPermission, getFullName } from "@shared/utils";
import { PERMISSION } from "@shared/constants";
import { selectors as sharedSelectors } from "@shared/store";
import { actions as SparksActions, selectors as SparksSelectors } from "@containers/Recognition/store/sparks";

import { Tooltip } from "../../../../shared/components/Tooltip";
import PersonSparksDrawerBody from "./PersonSparksDrawerBody";

import "./index.scss";

interface PersonSparksDrawerContainerProps {
  onClose: () => void;
  userId: number;
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md PR2: the read-only "a person's Sparks" drawer (`OVERLAY_TYPES.PERSON_SPARKS`),
 * opened from the Redemptions tab's ledger rows and Pending requests' "Who" name. Mirrors
 * RecognitionCreateContainer's own shell/wiring pattern (hooks, not connect(); `onClose` received as a
 * prop, injected by OverlayWrapper -- this container never dispatches `hideOverlay` itself).
 *
 * §6: dispatches `getPersonSparks.request(userId)` on mount and `clearPersonSparks()` on unmount (and
 * again on any later `userId` change, covering the same overlay instance being re-opened for a different
 * person without an intervening close). No global loader -- the tab behind this drawer must not flash.
 */
const PersonSparksDrawerContainer: FC<PersonSparksDrawerContainerProps> = (props) => {
  const { onClose, userId } = props;

  const dispatch = useDispatch();

  const data = useSelector(SparksSelectors.getPersonSparksData());
  const loading = useSelector(SparksSelectors.getPersonSparksLoading());
  const failed = useSelector(SparksSelectors.getPersonSparksFailed());

  // §7 PR3 / plan's own rule: same write gate as the pending table's own Actions column
  // (RedemptionsContainer.tsx), never this tab's read-only VIEW_REDEMPTIONS.
  const currentUser = useSelector(sharedSelectors.getUserDetails());
  const hasManageRedemptions = checkPermission(PERMISSION.MANAGE_REDEMPTIONS, currentUser ?? undefined);

  // Reuses the SAME in-flight set the pending table's own row buttons read -- one redux-held array
  // covers every decision in flight anywhere, table or drawer (plan's own instruction).
  const redemptionRequestDecisionInFlightIds = useSelector(SparksSelectors.getRedemptionRequestDecisionInFlightIds());
  const inFlightIds = useMemo(
    () => new Set(redemptionRequestDecisionInFlightIds),
    [redemptionRequestDecisionInFlightIds],
  );

  const handleApprove = useCallback(
    (id: number) => {
      dispatch(SparksActions.approveRedemptionRequest.request({ id }));
    },
    [dispatch],
  );

  const handleDeny = useCallback(
    (id: number, reason: string, cb: () => void) => {
      dispatch(SparksActions.denyRedemptionRequest.request({ id, reason, cb }));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(SparksActions.getPersonSparks.request(userId));

    return () => {
      dispatch(SparksActions.clearPersonSparks());
    };
  }, [dispatch, userId]);

  return (
    <div className="person-sparks-drawer">
      <div className="person-sparks-drawer-header">
        {data ? (
          <div className="person-sparks-drawer-person">
            <InitialsAvatar firstName={data.firstName} lastName={data.lastName} size={44} imageUrl={data.avatarUrl} />
            <div className="person-sparks-drawer-person-text">
              <div className="person-sparks-drawer-name">
                {getFullName({ first_name: data.firstName, last_name: data.lastName })}
              </div>
              {/* Team line renders only when teamName is non-null (§4/§5 -- "Engineering" is the team,
                  not job_title). */}
              {data.teamName ? <div className="person-sparks-drawer-team">{data.teamName}</div> : null}
            </div>
          </div>
        ) : (
          <div />
        )}
        <Tooltip text="Close">
          <button type="button" className="person-sparks-drawer-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </Tooltip>
      </div>
      <div className="person-sparks-drawer-body">
        {data ? (
          <PersonSparksDrawerBody
            data={data}
            hasManageRedemptions={hasManageRedemptions}
            inFlightIds={inFlightIds}
            onApprove={handleApprove}
            onDeny={handleDeny}
          />
        ) : loading ? (
          <div className="person-sparks-drawer-loading" data-testid="person-sparks-drawer-loading">
            Loading…
          </div>
        ) : failed ? (
          <div className="person-sparks-drawer-failed" data-testid="person-sparks-drawer-failed" role="alert">
            We couldn&apos;t load this person&apos;s Sparks.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PersonSparksDrawerContainer;
